import { ErrorBoundary as SentryErrorBoundaryComponent } from "@sentry/react";
import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";


/**
 * Default error fallback UI component
 */
function ErrorFallback({ 
  error, 
  resetError,
  componentStack,
  eventId 
}: { 
  error: unknown; 
  resetError: () => void;
  componentStack?: string;
  eventId?: string;
}) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const handleGoHome = () => {
    window.location.href = "/";
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-destructive/50 bg-card p-6 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-destructive/10 p-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">An unexpected error occurred</p>
          </div>
        </div>

        {import.meta.env.DEV && (
          <div className="rounded-md bg-muted p-3 space-y-2">
            <p className="text-xs font-mono text-muted-foreground break-words">
              {errorMessage || "Unknown error"}
            </p>
            {eventId && (
              <p className="text-xs text-muted-foreground">
                Event ID: {eventId}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={resetError} variant="default" className="flex-1">
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
          <Button onClick={handleGoHome} variant="outline" className="flex-1">
            <Home className="h-4 w-4" />
            Go home
          </Button>
          <Button onClick={handleReload} variant="ghost" className="flex-1">
            Reload page
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          The error has been reported. If this persists, please contact support.
        </p>
      </div>
    </div>
  );
}

/**
 * Convenience wrapper using Sentry's ErrorBoundary
 * This provides automatic error reporting to Sentry
 */
export function SentryErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <SentryErrorBoundaryComponent
      fallback={({ error, resetError, componentStack, eventId }) => (
        <ErrorFallback 
          error={error} 
          resetError={resetError}
          componentStack={componentStack}
          eventId={eventId}
        />
      )}
      showDialog={false}
      beforeCapture={(scope, error, componentStack) => {
        // Add additional context if needed
        scope.setContext("errorBoundary", {
          componentStack,
        });
      }}
    >
      {children}
    </SentryErrorBoundaryComponent>
  );
}
